import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import { EventRecord } from "../EventRecord";

import { eventQueryKeys } from "./eventQueryKeys";

/**
 * Bulk-delete events in a single request to the backend bulk-delete endpoint.
 *
 * Use this anywhere multiple events are selected and deleted together. The
 * deletion is scoped to the current org unless `orgId` names another. On
 * success, every event list view of that org is marked stale so the removed
 * events disappear.
 *
 * @param orgId The org that owns the events, such as the org of the session
 *   they belong to. Defaults to the current org.
 */
export function useDeleteEvents(orgId?: string) {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();
  const currentOrgId = useCurrentOrgId();
  const resolvedOrgId = orgId ?? currentOrgId;

  return useMutation<void, Error, EventRecord["event_id"][]>({
    mutationFn: (eventIds) =>
      events.deleteEvents(eventIds, { resourceOwnerId: resolvedOrgId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(resolvedOrgId),
      });
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(resolvedOrgId),
        refetchType: "none",
      });
    },
  });
}
